import { FC, useMemo } from 'react';
import { Badge, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { EmotionalArc } from 'types/story';
import { EMOTIONAL_ARCS } from '../../../data/emotionalArcs';


interface EmotionalArcSelectorProps {
  value?: string;
  onChange: (value: string | null) => void;
}

export const EmotionalArcSelector: FC<EmotionalArcSelectorProps> = ({ 
  value, 
  onChange,
}) => {
  const selectedArc = useMemo(() => {
    return value ? EMOTIONAL_ARCS.find(arc => arc.code === value) : null;
  }, [value]);

  const handleSelect = (arc: EmotionalArc) => {
    onChange(arc.code);
  };

  const handleClear = () => {
    onChange(null);
  };
  return (
    <Stack gap={2}>
      <>
        <Typography variant="body2" fontWeight={500} mb={1}>Emotional Arc</Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Optional framework for your story's emotional journey
        </Typography>
      </>
      <Stack spacing={2}>
        {selectedArc && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            p: 1.5, 
            border: '1px solid', 
            borderColor: 'primary.light', 
            bgcolor: 'primary.lighter', 
            borderRadius: 1, 
          }}>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }}/>
                <Typography variant="body2" fontWeight={500}>{selectedArc.name}</Typography>
                <Badge variant="standard">{selectedArc.shape}</Badge>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedArc.emotionalJourney}
              </Typography>
            </Box>
            <Button 
              variant="text" 
              size="small" 
              onClick={handleClear}
              sx={{ ml: 1 }}
            >
              Clear
            </Button>
          </Box>
        )}

        {!selectedArc && (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr', 
            gap: 1, 
            maxHeight: '16rem', 
            overflowY: 'auto', 
          }}>
            {EMOTIONAL_ARCS.map((arc) => (
              <Card 
                key={arc.code} 
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { boxShadow: 1 }, 
                  transition: 'box-shadow 0.2s', 
                  border: '1px solid', 
                  borderColor: 'divider', 
                }}
                onClick={() => handleSelect(arc)}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <CircleIcon sx={{ fontSize: 12, color: 'text.secondary' }}/>
                        <Typography variant="body2" fontWeight={500}>{arc.name}</Typography>
                        <Badge variant="standard">{arc.shape}</Badge>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        {arc.emotionalJourney}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
                        {arc.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
